import React from 'react';
import { Panel } from '@tanium/react-panel';
import { DefinitionList, DefinitionListItem } from '@tanium/react-definition-list';
import { useTranslation } from 'react-i18next';
import MediumText from 'components/styles/text/MediumText';
import MediumBoldText from 'components/styles/text/MediumBoldText';
import Customer from 'gql/Customer';
import { SafeCustomerEnvironmentStatus } from 'common/ICustomerEnvironmentStatus';
import Helper from 'utils/Helper';
import StatusIcon, { IIconTypes } from 'components/icons/StatusIcon';
import { Stack } from '@tanium/react-stack';
import Tooltip from 'components/tooltip/Tooltip';
import { Link } from '@tanium/react-link';

export interface ICustomerDetailsProps {
  customer?: Customer;
}
export const CustomerDetails = ({ customer }: ICustomerDetailsProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Panel title={t('CUSTOMER_INFO')}>
      <DefinitionList>
        <DefinitionListItem
          term={<MediumText>{t('ID')}:</MediumText>}
          description={<MediumBoldText>{customer?.id || '-'}</MediumBoldText>}
        />
        <DefinitionListItem
          term={<MediumText>{t('NAME')}:</MediumText>}
          description={<MediumBoldText>{customer?.name || '-'}</MediumBoldText>}
        />
        <DefinitionListItem
          term={<MediumText>{t('DESCRIPTION')}:</MediumText>}
          description={<MediumBoldText>{customer?.description || '-'}</MediumBoldText>}
        />
        <DefinitionListItem
          term={<MediumText>{t('TIER')}:</MediumText>}
          description={<MediumBoldText>{customer?.tier || '-'}</MediumBoldText>}
        />
        <DefinitionListItem
          term={<MediumText>{t('RING')}:</MediumText>}
          description={<MediumBoldText>{customer?.ring?.name || '-'}</MediumBoldText>}
        />
        <DefinitionListItem
          term={<MediumText>{t('STATUS')}:</MediumText>}
          description={
            <>
              {customer?.status && SafeCustomerEnvironmentStatus.indexOf(customer.status) === -1 ? (
                <Tooltip content={t('CUSTOMER_IS_NOT_READY')}>
                  <Stack itemSpacing={1}>
                    <MediumBoldText>{Helper.humanReadableStatus(customer?.status)}</MediumBoldText>
                    <StatusIcon type={IIconTypes.WARNING} props={{ outerRadius: 9, innerRadius: 6 }} />
                  </Stack>
                </Tooltip>
              ) : (
                <MediumBoldText>{Helper.humanReadableStatus(customer?.status) || '-'}</MediumBoldText>
              )}
            </>
          }
        />
        <DefinitionListItem
          term={<MediumText>{t('TANIUM_CONSOLE')}:</MediumText>}
          description={
            <MediumBoldText>
              <Link target="_blank" rel="noreferrer" href={customer?.domain && `https://${customer?.domain}`}>
                {customer?.domain || '-'}
              </Link>
            </MediumBoldText>
          }
        />
        <DefinitionListItem
          term={<MediumText>{t('PROVISIONER')}:</MediumText>}
          description={
            <MediumBoldText>
              <Link
                target="_blank"
                rel="noreferrer"
                href={customer?.id && customer?.host && Helper.ceLink(customer.id, customer.host)}
              >
                {(customer?.id && customer?.host && Helper.ceLink(customer.id, customer.host)) || '-'}
              </Link>
            </MediumBoldText>
          }
        />
      </DefinitionList>
    </Panel>
  );
};

export default CustomerDetails;
